import React, { useEffect, useMemo, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { LogOut, Mail, Phone, Download, Search } from "lucide-react";
import AdminBlogPanel from "../components/AdminBlogPanel";

const STATUSES = ["new", "contacted", "interview", "hired", "rejected"];
const STATUS_COLORS = {
  new: "border-[#121275] text-[#121275]",
  contacted: "border-blue-400 text-blue-400",
  interview: "border-purple-400 text-purple-400",
  hired: "border-green-400 text-green-400",
  rejected: "border-red-400 text-red-400",
};

function toCSV(rows, columns) {
  const escape = (v) => {
    if (v === null || v === undefined) return "";
    const s = String(v).replace(/"/g, '""');
    return /[",\n]/.test(s) ? `"${s}"` : s;
  };
  const header = columns.map((c) => c.label).join(",");
  const body = rows
    .map((r) => columns.map((c) => escape(typeof c.value === "function" ? c.value(r) : r[c.value])).join(","))
    .join("\n");
  return `${header}\n${body}`;
}

function downloadCSV(filename, content) {
  const blob = new Blob([content], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export default function AdminDashboardPage() {
  const { user, loading, logout } = useAuth();
  const [tab, setTab] = useState("applications");
  const [apps, setApps] = useState([]);
  const [leads, setLeads] = useState([]);
  const [shares, setShares] = useState([]);
  const [busy, setBusy] = useState(true);

  // Filters
  const [appStatus, setAppStatus] = useState("all");
  const [appQuery, setAppQuery] = useState("");
  const [leadQuery, setLeadQuery] = useState("");

  useEffect(() => {
    if (!user || user === false || user.role !== "admin") return;
    setBusy(true);
    Promise.all([
      api.get("/admin/applications").then((r) => setApps(r.data)).catch(() => setApps([])),
      api.get("/admin/leads").then((r) => setLeads(r.data)).catch(() => setLeads([])),
      api.get("/admin/calculator-shares").then((r) => setShares(r.data)).catch(() => setShares([])),
    ]).finally(() => setBusy(false));
  }, [user]);

  const filteredApps = useMemo(() => {
    const q = appQuery.trim().toLowerCase();
    return apps.filter((a) => {
      if (appStatus !== "all" && a.status !== appStatus) return false;
      if (!q) return true;
      return (
        a.full_name?.toLowerCase().includes(q) ||
        a.email?.toLowerCase().includes(q) ||
        a.phone?.toLowerCase().includes(q) ||
        a.license_state?.toLowerCase().includes(q) ||
        a.current_brokerage?.toLowerCase().includes(q)
      );
    });
  }, [apps, appStatus, appQuery]);

  const filteredLeads = useMemo(() => {
    const q = leadQuery.trim().toLowerCase();
    if (!q) return leads;
    return leads.filter(
      (l) =>
        l.name?.toLowerCase().includes(q) ||
        l.email?.toLowerCase().includes(q) ||
        l.interest?.toLowerCase().includes(q)
    );
  }, [leads, leadQuery]);

  if (loading) return <div className="min-h-screen bg-[#ffffff]" />;
  if (!user || user === false || user.role !== "admin") return <Navigate to="/admin/login" replace />;

  const updateStatus = async (id, statusNew) => {
    try {
      const { data } = await api.patch(`/admin/applications/${id}`, { status: statusNew });
      setApps((arr) => arr.map((a) => (a.id === id ? data : a)));
    } catch (_) {}
  };

  const exportAppsCSV = () => {
    const cols = [
      { label: "Submitted", value: (r) => new Date(r.created_at).toISOString() },
      { label: "Name", value: "full_name" },
      { label: "Email", value: "email" },
      { label: "Phone", value: "phone" },
      { label: "License State", value: "license_state" },
      { label: "Years", value: "years_experience" },
      { label: "Current Brokerage", value: "current_brokerage" },
      { label: "Volume", value: "annual_volume" },
      { label: "Message", value: "message" },
      { label: "Status", value: "status" },
      { label: "ID", value: "id" },
    ];
    downloadCSV(`soldby1stchoice-applications-${Date.now()}.csv`, toCSV(filteredApps, cols));
  };

  const exportLeadsCSV = () => {
    const cols = [
      { label: "Submitted", value: (r) => new Date(r.created_at).toISOString() },
      { label: "Name", value: "name" },
      { label: "Email", value: "email" },
      { label: "Phone", value: "phone" },
      { label: "Interest", value: "interest" },
      { label: "ID", value: "id" },
    ];
    downloadCSV(`soldby1stchoice-leads-${Date.now()}.csv`, toCSV(filteredLeads, cols));
  };

  const exportSharesCSV = () => {
    const cols = [
      { label: "Submitted", value: (r) => new Date(r.created_at).toISOString() },
      { label: "Name", value: "name" },
      { label: "Email", value: "email" },
      { label: "GCI", value: "gci" },
      { label: "Transactions", value: "transactions" },
      { label: "Current Split %", value: "current_split" },
      { label: "Current Fees / mo", value: "current_fees" },
      { label: "Sold By 1st Choice Net", value: "first_choice_net" },
      { label: "Current Net", value: "current_net" },
      { label: "Difference", value: "diff" },
      { label: "Share URL", value: "share_url" },
    ];
    downloadCSV(`soldby1stchoice-calculator-leads-${Date.now()}.csv`, toCSV(shares, cols));
  };

  const stats = {
    total: apps.length,
    new: apps.filter((a) => a.status === "new").length,
    interview: apps.filter((a) => a.status === "interview").length,
    hired: apps.filter((a) => a.status === "hired").length,
  };

  return (
    <div data-testid="admin-dashboard-page" className="min-h-screen bg-[#ffffff] text-[#0a0a0a]">
      <header className="border-b border-black/10 bg-white/80 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img
              src="/logo-transparent.png"
              alt="1st Choice Realty & Associates"
              className="h-12 w-auto"
            />
            <div className="numeric text-xs tracking-widest uppercase text-black/70">
              Admin Console
            </div>
          </Link>
          <div className="flex items-center gap-4">
            <span className="numeric text-xs uppercase tracking-widest text-black/50">
              {user.email}
            </span>
            <button
              onClick={logout}
              data-testid="admin-logout"
              className="text-black/70 hover:text-[#0a0a0a] inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest"
            >
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-12">
        <div className="overline">Recruiting console</div>
        <h1 className="font-display text-3xl sm:text-4xl text-[#0a0a0a] font-light tracking-tight mt-2">
          Pipeline overview
        </h1>

        <div className="mt-8 grid grid-cols-2 sm:grid-cols-5 gap-4">
          <Stat label="Total apps" value={stats.total} />
          <Stat label="New" value={stats.new} highlight />
          <Stat label="Interviewing" value={stats.interview} />
          <Stat label="Hired" value={stats.hired} />
          <Stat label="Info leads" value={leads.length + shares.length} />
        </div>

        <div className="mt-12 border-b border-black/10 flex flex-wrap gap-1">
          <TabButton active={tab === "applications"} onClick={() => setTab("applications")} testid="tab-applications">
            Applications ({apps.length})
          </TabButton>
          <TabButton active={tab === "leads"} onClick={() => setTab("leads")} testid="tab-leads">
            Info leads ({leads.length})
          </TabButton>
          <TabButton active={tab === "shares"} onClick={() => setTab("shares")} testid="tab-shares">
            Calc leads ({shares.length})
          </TabButton>
          <TabButton active={tab === "blog"} onClick={() => setTab("blog")} testid="tab-blog">
            Blog
          </TabButton>
        </div>

        <div className="mt-8">
          {busy && tab !== "blog" && <div className="text-black/50 numeric text-sm">Loading…</div>}

          {!busy && tab === "applications" && (
            <>
              <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between mb-6">
                <div className="flex flex-col sm:flex-row gap-3 flex-1">
                  <div className="relative flex-1 max-w-xs">
                    <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-black/30" />
                    <input
                      type="text"
                      placeholder="Search name, email, state…"
                      value={appQuery}
                      onChange={(e) => setAppQuery(e.target.value)}
                      data-testid="apps-search"
                      className="bg-[#f7f7f7] border border-black/15 text-sm text-[#0a0a0a] pl-9 pr-3 py-2.5 w-full font-mono outline-none focus:border-[#121275] transition-colors"
                    />
                  </div>
                  <select
                    value={appStatus}
                    onChange={(e) => setAppStatus(e.target.value)}
                    data-testid="apps-filter-status"
                    className="bg-[#f7f7f7] border border-black/15 numeric text-xs uppercase tracking-widest px-3 py-2.5 text-[#0a0a0a]"
                  >
                    <option value="all" className="bg-white text-black">All statuses</option>
                    {STATUSES.map((s) => (
                      <option key={s} value={s} className="bg-white text-black">{s}</option>
                    ))}
                  </select>
                </div>
                <button
                  onClick={exportAppsCSV}
                  data-testid="apps-export-csv"
                  className="btn-secondary !py-2 !px-4 text-xs uppercase tracking-widest"
                >
                  <Download size={14} /> Export CSV
                </button>
              </div>

              <div data-testid="applications-list" className="overflow-x-auto">
                {filteredApps.length === 0 ? (
                  <Empty label={apps.length === 0 ? "No applications yet." : "No applications match your filter."} />
                ) : (
                  <table className="w-full text-left">
                    <thead>
                      <tr className="border-b border-black/10 text-[10px] uppercase tracking-widest text-black/40 font-mono">
                        <th className="py-3 pr-4">Name</th>
                        <th className="py-3 pr-4">Contact</th>
                        <th className="py-3 pr-4">Lic.</th>
                        <th className="py-3 pr-4">Yrs</th>
                        <th className="py-3 pr-4">Brokerage</th>
                        <th className="py-3 pr-4">Volume</th>
                        <th className="py-3 pr-4">Submitted</th>
                        <th className="py-3 pr-4">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredApps.map((a) => (
                        <tr
                          key={a.id}
                          data-testid={`application-row-${a.id}`}
                          className="border-b border-black/5 hover:bg-white/[0.02]"
                        >
                          <td className="py-4 pr-4">
                            <div className="text-[#0a0a0a]">{a.full_name}</div>
                            <div className="numeric text-[10px] text-black/40 mt-1">{a.id.slice(0, 8)}</div>
                          </td>
                          <td className="py-4 pr-4 text-sm text-black/70">
                            <div className="flex items-center gap-2"><Mail size={12} className="text-[#121275]" />{a.email}</div>
                            <div className="flex items-center gap-2 mt-1"><Phone size={12} className="text-[#121275]" />{a.phone}</div>
                          </td>
                          <td className="py-4 pr-4 numeric text-sm text-[#0a0a0a]">{a.license_state}</td>
                          <td className="py-4 pr-4 numeric text-sm text-[#0a0a0a]">{a.years_experience}</td>
                          <td className="py-4 pr-4 text-sm text-black/70">{a.current_brokerage || "—"}</td>
                          <td className="py-4 pr-4 numeric text-xs text-black/70">{a.annual_volume || "—"}</td>
                          <td className="py-4 pr-4 numeric text-xs text-black/50">
                            {new Date(a.created_at).toLocaleDateString()}
                          </td>
                          <td className="py-4 pr-4">
                            <select
                              value={a.status}
                              onChange={(e) => updateStatus(a.id, e.target.value)}
                              data-testid={`status-select-${a.id}`}
                              className={`bg-transparent border numeric text-[10px] uppercase tracking-widest px-2 py-1.5 ${STATUS_COLORS[a.status] || "border-black/15 text-[#0a0a0a]"}`}
                            >
                              {STATUSES.map((s) => (
                                <option key={s} value={s} className="bg-white text-black">{s}</option>
                              ))}
                            </select>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </>
          )}

          {!busy && tab === "leads" && (
            <>
              <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between mb-6">
                <div className="relative flex-1 max-w-xs">
                  <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-black/30" />
                  <input
                    type="text"
                    placeholder="Search…"
                    value={leadQuery}
                    onChange={(e) => setLeadQuery(e.target.value)}
                    data-testid="leads-search"
                    className="bg-[#f7f7f7] border border-black/15 text-sm text-[#0a0a0a] pl-9 pr-3 py-2.5 w-full font-mono outline-none focus:border-[#121275] transition-colors"
                  />
                </div>
                <button
                  onClick={exportLeadsCSV}
                  data-testid="leads-export-csv"
                  className="btn-secondary !py-2 !px-4 text-xs uppercase tracking-widest"
                >
                  <Download size={14} /> Export CSV
                </button>
              </div>

              <div data-testid="leads-list" className="overflow-x-auto">
                {filteredLeads.length === 0 ? (
                  <Empty label={leads.length === 0 ? "No leads yet." : "No leads match."} />
                ) : (
                  <table className="w-full text-left">
                    <thead>
                      <tr className="border-b border-black/10 text-[10px] uppercase tracking-widest text-black/40 font-mono">
                        <th className="py-3 pr-4">Name</th>
                        <th className="py-3 pr-4">Email</th>
                        <th className="py-3 pr-4">Phone</th>
                        <th className="py-3 pr-4">Interest</th>
                        <th className="py-3 pr-4">Submitted</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredLeads.map((l) => (
                        <tr key={l.id} data-testid={`lead-row-${l.id}`} className="border-b border-black/5">
                          <td className="py-4 pr-4 text-[#0a0a0a]">{l.name}</td>
                          <td className="py-4 pr-4 text-sm text-black/70">{l.email}</td>
                          <td className="py-4 pr-4 numeric text-xs text-black/70">{l.phone || "—"}</td>
                          <td className="py-4 pr-4 numeric text-[10px] uppercase tracking-widest text-[#121275]">
                            {l.interest}
                          </td>
                          <td className="py-4 pr-4 numeric text-xs text-black/50">
                            {new Date(l.created_at).toLocaleDateString()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </>
          )}

          {!busy && tab === "shares" && (
            <>
              <div className="flex items-center justify-between mb-6">
                <div className="numeric text-xs text-black/50 uppercase tracking-widest">
                  Recruits who asked for a PDF of their calculator results
                </div>
                <button
                  onClick={exportSharesCSV}
                  data-testid="shares-export-csv"
                  className="btn-secondary !py-2 !px-4 text-xs uppercase tracking-widest"
                >
                  <Download size={14} /> Export CSV
                </button>
              </div>
              <div data-testid="shares-list" className="overflow-x-auto">
                {shares.length === 0 ? (
                  <Empty label="No calculator leads yet." />
                ) : (
                  <table className="w-full text-left">
                    <thead>
                      <tr className="border-b border-black/10 text-[10px] uppercase tracking-widest text-black/40 font-mono">
                        <th className="py-3 pr-4">Name</th>
                        <th className="py-3 pr-4">Email</th>
                        <th className="py-3 pr-4">GCI</th>
                        <th className="py-3 pr-4">Diff / yr</th>
                        <th className="py-3 pr-4">Submitted</th>
                      </tr>
                    </thead>
                    <tbody>
                      {shares.map((s) => (
                        <tr key={s.id} data-testid={`share-row-${s.id}`} className="border-b border-black/5">
                          <td className="py-4 pr-4 text-[#0a0a0a]">{s.name || "—"}</td>
                          <td className="py-4 pr-4 text-sm text-black/70">{s.email}</td>
                          <td className="py-4 pr-4 numeric text-sm text-[#0a0a0a]">${Number(s.gci).toLocaleString()}</td>
                          <td className="py-4 pr-4 numeric text-sm text-[#121275]">+${Number(s.diff).toLocaleString()}</td>
                          <td className="py-4 pr-4 numeric text-xs text-black/50">
                            {new Date(s.created_at).toLocaleDateString()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </>
          )}

          {tab === "blog" && <AdminBlogPanel />}
        </div>
      </main>
    </div>
  );
}

function Stat({ label, value, highlight }) {
  return (
    <div className={`bg-[#f7f7f7] border p-5 ${highlight ? "border-[#121275]/40" : "border-black/10"}`}>
      <div className="overline">{label}</div>
      <div className={`numeric text-3xl mt-2 ${highlight ? "text-[#121275]" : "text-[#0a0a0a]"}`}>{value}</div>
    </div>
  );
}

function TabButton({ active, onClick, children, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`px-5 py-3 text-xs font-mono uppercase tracking-widest border-b-2 transition-colors -mb-px ${
        active ? "border-[#121275] text-[#0a0a0a]" : "border-transparent text-black/50 hover:text-[#0a0a0a]"
      }`}
    >
      {children}
    </button>
  );
}

function Empty({ label }) {
  return (
    <div className="border border-dashed border-black/15 p-12 text-center text-black/50">{label}</div>
  );
}
